import { useCallback, useEffect, useMemo, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getStoredToken, onSessionExpired, setAuthToken, setUserTimezone, setWebsiteType } from '@/api/client'
import { authApi, type LoginResponse } from '@/api/auth'
import { STORAGE_KEYS, writeStorage } from '@/lib/storage'
import { AuthContext, ME_QUERY_KEY, type AuthContextValue } from './useAuth'

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const hasToken = !!getStoredToken()

  const meQuery = useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: authApi.me,
    enabled: hasToken,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  const clearSession = useCallback(() => {
    setAuthToken(null)
    setWebsiteType(null)
    queryClient.setQueryData(ME_QUERY_KEY, null)
    queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' })
  }, [queryClient])

  useEffect(() => onSessionExpired(clearSession), [clearSession])

  const applySession = useCallback(
    (session: LoginResponse) => {
      setAuthToken(`Bearer ${session.access_token}`)
      setWebsiteType(session.website_type_id)
      const timezone = session.timezone && session.timezone !== 'none' ? session.timezone : Intl.DateTimeFormat().resolvedOptions().timeZone
      setUserTimezone(timezone)
      void queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY })
    },
    [queryClient],
  )

  const login = useCallback(
    async (email: string, password: string, remember: boolean) => {
      const session = await authApi.login(email, password)
      applySession(session)
      writeStorage(STORAGE_KEYS.rememberedLogin, remember ? JSON.stringify({ useremail: email }) : null)
      await queryClient.fetchQuery({ queryKey: ME_QUERY_KEY, queryFn: authApi.me })
    },
    [applySession, queryClient],
  )

  const logout = useCallback(async () => {
    try {
      await authApi.logout()
    } catch {
      // The token may already be dead; local cleanup still has to happen.
    }
    clearSession()
  }, [clearSession])

  const refreshUser = useCallback(async () => {
    const result = await queryClient.fetchQuery({ queryKey: ME_QUERY_KEY, queryFn: authApi.me })
    return result ?? undefined
  }, [queryClient])

  const value = useMemo<AuthContextValue>(
    () => ({
      user: meQuery.data ?? null,
      initializing: hasToken && meQuery.isPending,
      isAuthenticated: !!meQuery.data,
      login,
      logout,
      applySession,
      refreshUser,
    }),
    [meQuery.data, meQuery.isPending, hasToken, login, logout, applySession, refreshUser],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
